import { useContext } from 'react'
import {
  IConversation,
  IMessage,
  IParticipant,
  IWebsocketMessage,
  StoreContextType,
  IMessageHandlers,
} from '@/context/types'
import { MESSAGE_TYPES } from '@/context/constants'

import Conversations from './Conversations'
import Loading from './Loading'
import Input from './Input'

const Messages = (context: React.Context<StoreContextType | null>) => {
  const store = useContext(context) as StoreContextType
  const {
    getConversationList,
    setConversationList,
    getSelectedConversation,
    setSelectedConversation,
    findConversation,
  } = Conversations(context)
  const { sendMessage } = Input(context)

  const { getLoading, setLoading } = Loading(context)

  const getForceUpdate = () => store?.force.forceUpdate
  const setForceUpdate = () => store.force.setForceUpdate(prev => !prev)

  const getConversationMessages = (conversationId: string) => {
    const conversationList = getConversationList()

    const conversation = conversationList.find(
      conversation => conversation.id === conversationId,
    )

    if (!conversation) {
      return []
    } else {
      return conversation.messages
    }
  }

  const setConversationMessages = (
    conversationId: string,
    messages: IMessage[],
    append?: boolean,
  ) => {
    const conversationList = [...getConversationList()]
    const conversation = conversationList.find(
      conversation => conversation.id === conversationId,
    )

    // If first item, put in list
    if (!conversation) {
      const newConversation: IConversation = {
        id: conversationId,
        displayName: '',
        description: '',
        isDefault: false,
        lastUpdatedAt: '',
        messages,
        unreadCount: null,
      }

      store.conversations.setConversationList(prev => [
        ...prev,
        newConversation,
      ])
    } else {
      conversation.messages = append
        ? [...conversation.messages, ...messages]
        : [...messages]

      store.conversations.setConversationList(conversationList)
    }
  }

  const getLastConversationMessage = (conversationId?: string) => {
    const conversationMessages = getConversationMessages(
      conversationId || getSelectedConversation(),
    )

    return conversationMessages[conversationMessages.length - 1]
  }

  const initMessage = (wsMessage: IWebsocketMessage) => {
    console.log('WEBSOCKET INIT SUCCESS')
    store.init.setIsInit(true)
    store.errors.setErrors([])

    const { firstName, lastName } = wsMessage.data || {
      firstName: '',
      lastName: '',
    }

    store.name.setUserObj(() => ({ firstName, lastName }))
  }

  // Get conversations list
  const getConversationsMessage = (wsMessage: IWebsocketMessage) => {
    const conversations = wsMessage.data.conversations

    setConversationList(conversations ? [...conversations] : [], false)

    setLoading({ ...getLoading(), conversations: false })

    for (const conversation of conversations) {
      sendMessage({
        type: MESSAGE_TYPES.GET_LAST_MESSAGE,
        conversationId: conversation.id,
      })
    }
  }

  // Get one conversations info
  const getConversationMessage = (wsMessage: IWebsocketMessage) => {
    setConversationList(
      wsMessage?.data?.conversation ? [wsMessage.data.conversation] : [],
      true,
    )

    sendMessage({
      type: MESSAGE_TYPES.GET_LAST_MESSAGE,
      conversationId: wsMessage.data.conversation.id,
    })
  }

  const updateConversation = (wsMessage: IWebsocketMessage) => {
    const { id, lastUpdatedAt, displayName, description, metadata } = wsMessage
      .data.conversation as IConversation
    const conversation = findConversation(id)

    if (conversation) {
      if (displayName) {
        conversation.displayName = displayName
      }
      if (description) {
        conversation.description = description
      }
      if (lastUpdatedAt) {
        conversation.lastUpdatedAt = lastUpdatedAt
      }
      if (metadata) {
        conversation.metadata = { ...conversation.metadata, ...metadata }
      }
      setForceUpdate()
    }
  }

  const getMessagesMessage = (wsMessage: IWebsocketMessage) => {
    setConversationMessages(
      getSelectedConversation(),
      wsMessage.data && wsMessage.data.messages
        ? [...wsMessage.data.messages]
        : [],
      false,
    )

    setLoading({ ...getLoading(), messages: false })

    if (getSelectedConversation()) {
      const messages = wsMessage.data.messages

      sendMessage({
        type: MESSAGE_TYPES.UPDATE_CONVERSATION_READ_INDEX,
        messageIndex: messages[messages.length - 1]?.index || 0,
        conversationId: getSelectedConversation(),
      })
    }
  }

  const messageMessage = (wsMessage: IWebsocketMessage) => {
    const { metadata, conversationId } = wsMessage.data || { metadata: {} }
    const messages = getConversationMessages(
      conversationId || getSelectedConversation(),
    )
    const message = messages.find(message => message.id === wsMessage.data.id)

    const userExternalId = store.config.objConfig?.userExternalId

    // Do not add if existing
    if (
      message ||
      !metadata?.to?.find((to: any) => to.userExternalId === userExternalId)
    ) {
      return
    }

    setConversationMessages(
      conversationId || getSelectedConversation(),
      [{ ...wsMessage.data }],
      true,
    )

    const conversation = findConversation(
      conversationId || getSelectedConversation(),
    )

    if (conversation) {
      conversation.metadata.lastMessage = wsMessage.data

      conversation.metadata.isRead =
        (conversation.metadata.participant.lastReadMessageIndex || 0) >=
        (conversation.metadata.lastMessage.index || 0)
    }

    if (getSelectedConversation() === conversationId) {
      sendMessage({
        type: MESSAGE_TYPES.UPDATE_CONVERSATION_READ_INDEX,
        messageIndex: wsMessage.data.index,
        conversationId: getSelectedConversation(),
      })
    }

    setForceUpdate()
  }

  const newConversationMessage = (wsMessage: IWebsocketMessage) => {
    const conversationId = wsMessage.data.id

    const conversation = findConversation(conversationId)
    if (!conversation) {
      setConversationList(wsMessage?.data ? [wsMessage.data] : [], true)
      setForceUpdate()
    }
    setSelectedConversation(conversationId)
  }

  // Do nothing for now
  const subscribeConversationMessage = (wsMessage: IWebsocketMessage) => {
    console.log('WEBSOCKET SUBSCRIBED TO CONVERSATION')
    const { participants, assignedVendor } = wsMessage.data as {
      participants: IParticipant[]
      assignedVendor: any
    }
    const conversation = findConversation(getSelectedConversation())

    if (conversation) {
      conversation.participants = participants
      conversation.metadata = { ...conversation.metadata, assignedVendor }
      setForceUpdate()
    }

    return
  }
  const unsubscribeConversationMessage = () => {
    console.log('WEBSOCKET UNSUBSCRIBED FROM CONVERSATION')
    return
  }
  const subscribeUserMessage = () => {
    console.log('WEBSOCKET SUBSCRIBED USER')
    return
  }
  const unsubscribeUserMessage = () => {
    console.log('WEBSOCKET UNSUBSCRIBED USER')
    return
  }
  const heartbeatMessage = () => {
    console.log('WEBSOCKET HEARTBEAT')
    return
  }
  const typingStartMessage = () => {
    store?.typing.setIsTyping(true)
    return
  }
  const typingStopMessage = () => {
    store?.typing.setIsTyping(false)
    return
  }
  const reopenCaseMessage = () => {
    console.log('WEBSOCKET REOPENED CASE')
    return
  }
  const updateConversationReadIndexMessage = (wsMessage: IWebsocketMessage) => {
    console.log('WEBSOCKET UPDATED CONVERSATION LAST READ INDEX')
    const { id, unreadCount } = wsMessage.data.conversation as IConversation
    const conversation = findConversation(id)

    if (conversation && conversation.id === getSelectedConversation()) {
      conversation.unreadCount = unreadCount

      if (conversation?.metadata?.participant) {
        conversation.metadata.participant.lastReadMessageIndex =
          conversation.metadata?.lastMessage?.index || 0
      }

      if (conversation.metadata) {
        conversation.metadata.isRead =
          (conversation.metadata?.participant?.lastReadMessageIndex || 0) >=
          (conversation.metadata?.lastMessage?.index || 0)
      }

      setForceUpdate()
    }
    return
  }
  const getFaqMessage = (wsMessage: IWebsocketMessage) => {
    const { body } = wsMessage.data

    store.faq.setFaqBody(body)

    setLoading({ ...getLoading(), faq: false })
    return
  }
  const getLastMessageMessage = (wsMessage: IWebsocketMessage) => {
    const conversationId = wsMessage.data.conversationId
    const conversation = findConversation(conversationId)

    if (conversation) {
      conversation.metadata.lastMessage = wsMessage.data?.lastMessage
      conversation.metadata.participant = wsMessage.data?.participant

      conversation.metadata.isRead =
        (conversation.metadata?.participant?.lastReadMessageIndex || 0) >=
        (conversation.metadata?.lastMessage?.index || 0)

      setForceUpdate()
    }

    return
  }

  const MESSAGE_HANDLERS: IMessageHandlers = {
    [MESSAGE_TYPES.INIT]: initMessage,
    [MESSAGE_TYPES.NEW_CONVERSATION]: newConversationMessage,
    [MESSAGE_TYPES.UPDATE_CONVERSATION]: updateConversation,
    [MESSAGE_TYPES.GET_CONVERSATION]: getConversationMessage,
    [MESSAGE_TYPES.GET_CONVERSATIONS]: getConversationsMessage,
    [MESSAGE_TYPES.GET_MESSAGES]: getMessagesMessage,
    [MESSAGE_TYPES.MESSAGE]: messageMessage,
    [MESSAGE_TYPES.SUBSCRIBE_CONVERSATION]: subscribeConversationMessage,
    [MESSAGE_TYPES.UNSUBSCRIBE_CONVERSATION]: unsubscribeConversationMessage,
    [MESSAGE_TYPES.SUBSCRIBE_USER]: subscribeUserMessage,
    [MESSAGE_TYPES.UNSUBSCRIBE_USER]: unsubscribeUserMessage,
    [MESSAGE_TYPES.HEARTBEAT]: heartbeatMessage,
    [MESSAGE_TYPES.TYPING_START]: typingStartMessage,
    [MESSAGE_TYPES.TYPING_STOP]: typingStopMessage,
    [MESSAGE_TYPES.REOPEN_CASE]: reopenCaseMessage,
    [MESSAGE_TYPES.GET_FAQ]: getFaqMessage,
    [MESSAGE_TYPES.UPDATE_CONVERSATION_READ_INDEX]:
      updateConversationReadIndexMessage,
    [MESSAGE_TYPES.GET_LAST_MESSAGE]: getLastMessageMessage,
  }

  return {
    getConversationMessages,
    setConversationMessages,
    getLastConversationMessage,
    MESSAGE_HANDLERS,
    getForceUpdate,
    setForceUpdate,
  }
}

export default Messages
